import type { IconKind } from '../weather';

// Line icons on a 24-unit grid, drawn in currentColor so they take the text
// colour of wherever they sit.

// Cloud with its base at y=18. Precipitation icons lift it to make room for
// the marks underneath; the sun-and-cloud variants lift less so the sun's top
// ray stays inside the box, and drop their marks to match.
const CLOUD = 'M6.5 18H17a4 4 0 0 0 .4-7.97 5.5 5.5 0 0 0-10.5 1.57A3.2 3.2 0 0 0 6.5 18z';

function rays(cx: number, cy: number, inner: number, outer: number, angles: number[]): string {
  return angles
    .map((deg) => {
      const a = (deg * Math.PI) / 180;
      const x1 = cx + inner * Math.cos(a);
      const y1 = cy + inner * Math.sin(a);
      const x2 = cx + outer * Math.cos(a);
      const y2 = cy + outer * Math.sin(a);
      return `M${x1.toFixed(2)} ${y1.toFixed(2)}L${x2.toFixed(2)} ${y2.toFixed(2)}`;
    })
    .join('');
}

const SUN = (
  <>
    <circle cx="12" cy="12" r="4" />
    <path d={rays(12, 12, 6.5, 8.5, [0, 45, 90, 135, 180, 225, 270, 315])} />
  </>
);

const MOON = <path d="M19.5 14.5A7.5 7.5 0 1 1 9.5 4.5a6 6 0 0 0 10 10z" />;

// A small sun peeking over the cloud's upper left, drawn only where the
// cloud doesn't cover it.
const PEEK_SUN = (
  <>
    <path d="M5.88 10.62A3 3 0 0 1 9.5 5.9" />
    <path d={rays(8, 8.5, 4.8, 6.3, [180, 225, 270])} />
  </>
);
const PEEK_MOON = <path d="M10.2 5.4A3.8 3.8 0 0 0 4.6 10.4a3.2 3.2 0 0 1 5.6-5z" />;
const PEEK_CLOUD = 'translate(2 1.5)';

function lifted(children: React.ReactNode, by = 3) {
  return <g transform={`translate(0 -${by})`}>{children}</g>;
}

const RAIN = <path d="M8.5 17.5l-1 3M12.5 17.5l-1 3M16.5 17.5l-1 3" />;
const DRIZZLE = <path d="M8 18.5h.01M12 20h.01M16 18.5h.01M10 21h.01M14 21.5h.01" />;
const SNOW = <path d="M7 17.5l2 2m0-2l-2 2M11 19.5l2 2m0-2l-2 2M15 17.5l2 2m0-2l-2 2" />;
const FREEZING = <path d="M8.5 17.5l-1 3M16.5 17.5l-1 3M12 18.5h.01M11.5 21h.01" />;
const BOLT = <path d="M13 15l-2.5 3.5h3L11 22" />;
const FOG = <path d="M5 18h14M7 21h10" />;

function glyph(kind: IconKind, night: boolean): React.ReactNode {
  const peek = night ? PEEK_MOON : PEEK_SUN;
  switch (kind) {
    case 'clear':
      return night ? MOON : SUN;
    case 'partly':
      return (
        <>
          {peek}
          <path d={CLOUD} transform={PEEK_CLOUD} />
        </>
      );
    case 'cloudy':
      return <path d={CLOUD} />;
    case 'fog':
      return (
        <>
          {lifted(<path d={CLOUD} />)}
          {FOG}
        </>
      );
    case 'drizzle':
      return (
        <>
          {lifted(<path d={CLOUD} />)}
          {DRIZZLE}
        </>
      );
    case 'rain':
      return (
        <>
          {lifted(<path d={CLOUD} />)}
          {RAIN}
        </>
      );
    case 'freezing':
      return (
        <>
          {lifted(<path d={CLOUD} />)}
          {FREEZING}
        </>
      );
    case 'snow':
      return (
        <>
          {lifted(<path d={CLOUD} />)}
          {SNOW}
        </>
      );
    case 'showers':
      return (
        <>
          {lifted(
            <>
              {peek}
              <path d={CLOUD} transform={PEEK_CLOUD} />
            </>,
            2
          )}
          <g transform="translate(0 1)">{RAIN}</g>
        </>
      );
    case 'snow_showers':
      return (
        <>
          {lifted(
            <>
              {peek}
              <path d={CLOUD} transform={PEEK_CLOUD} />
            </>,
            2
          )}
          <g transform="translate(0 1)">{SNOW}</g>
        </>
      );
    case 'thunder':
      return (
        <>
          {lifted(<path d={CLOUD} />)}
          {BOLT}
        </>
      );
  }
}

interface Props {
  kind: IconKind;
  night?: boolean;
  size?: number;
  className?: string;
  title?: string;
}

export default function WeatherIcon({ kind, night = false, size = 16, className, title }: Props) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {title && <title>{title}</title>}
      {glyph(kind, night)}
    </svg>
  );
}

// A raindrop for inline precipitation figures.
export function DropIcon({ size = 10, className }: { size?: number; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M12 3.5c3.5 4.5 6 7.9 6 11a6 6 0 0 1-12 0c0-3.1 2.5-6.5 6-11z" />
    </svg>
  );
}
